export class Percentile {
    p; // the percentile requested
    nMax; // the maximum data length expected
    lo; // equals to p<0.5
    nMaxInternal; // the maximum internal buffer length;
    threshold; // the lowest (when lo = false) or the highest (when lo = true) value in the
    // buffer
    n = 0; // the number of times data was added, i.e. the data set size until now
    buffer; // the internal buffer
    unsorted; // is the buffer sorted
    leading; // how many elements lower then those in the buffer have we processed
    tailing; // how many elements higher then those in the buffer have we processed
    sortCount; //keep track of how many times we had to sort; for performance measurement only
    constructor(p, nMax) {
        this.p = p;
        this.nMax = nMax;
        // the internal strategy to follow depends on this test
        // when lo is true, we keep the lowest values, when lo is false we keep the
        // highest values
        this.lo = this.p < 0.5;
        // calculate which lower index would apply when all data would be provided
        // this is how the spreadsheets do it
        const kf = (this.nMax - 1) * this.p + 1.0;
        const k = Math.floor(kf);
        // based on p and nMax and k we can determine the maximum internal buffer size
        // needed;
        this.nMaxInternal = (this.lo ? k : this.nMax - k) + 1;
        // initialize the buffer 'entry' threshold;
        this.threshold = undefined;
        // create the internal buffer
        this.buffer = [];
        // the empty buffer is sorted
        this.unsorted = false;
        // we start empty
        this.n = 0;
        this.leading = 0;
        this.tailing = 0;
        this.sortCount = 0;
    }
    static calc(data, percentile) {
        if (data.length == 0) {
            throw new Error("Set is empty.");
        }
        else if (data.length == 1) {
            return data[0];
        }
        else {
            // sort data
            data.sort();
            // percentile calculation
            const kf = (data.length - 1) * percentile + 1.0;
            const k = Math.floor(kf); // the index of the lower value on the interval
            const f = kf - k; // the interpolation factor
            // get the boundary values of the interval from data
            const Xk = data[k - 1];
            const XkPlus1 = data[k];
            // calculate the actual percentile
            const Xp = Xk * (1.0 - f) + XkPlus1 * f;
            return Xp;
        }
    }
    /**
     *
     * @return the percentile value of this set
     */
    get() {
        if (this.n == 0) {
            throw new Error("Set is empty.");
        }
        else {
            if (this.n == 1) {
                return this.threshold;
            }
            else {
                // sort the buffer if it is not unsorted
                if (this.unsorted) {
                    this.buffer.sort();
                    this.unsorted = false;
                }
                // percentile calculation
                const kf = (this.n - 1) * this.p + 1.0;
                const k = Math.floor(kf); // the index of the lower value on the interval
                const f = kf - k; // the interpolation factor
                // map index k to the matching index in the buffer
                const kBuf = k - this.leading - 1;
                // get the boundary values of the interval from the buffer
                const Xk = this.buffer[kBuf];
                if (f > 0.0) {
                    const XkPlus1 = this.buffer[kBuf + 1];
                    // calculate the actual percentile
                    const Xp = Xk * (1.0 - f) + XkPlus1 * f;
                    return Xp;
                }
                else {
                    const Xp = Xk;
                    return Xp;
                }
            }
        }
    }
    test() {
        if (this.leading + this.buffer.length + this.tailing != this.n) {
            throw new Error(`Expected leadings(${this.leading}) + buffer(${this.buffer.length}) + tailings(${this.tailing}) to be exual to ${this.n}.`);
        }
    }
    toString() {
        return `p: ${this.p}, n-max: ${this.nMax}, n-max-internal: ${this.nMaxInternal}, strategy: ${this.lo ? "LO" : "HI"}, n: ${this.n}, leading: ${this.leading}, size: ${this.buffer.length}, tailing: ${this.tailing}, ${this.lo ? "buffer-highest" : "buffer-lowest"}: ${this.threshold}, state: ${this.unsorted ? "un-sorted" : "sorted"}, n-sorted: ${this.sortCount}`;
    }
    /**
     * Add a new datum to the set where to get the percentile for
     *
     * @param datum the value to add to the set
     */
    add(datum) {
        if (this.n < this.nMax) {
            this.n++;
            if (this.buffer.length < this.nMaxInternal) {
                // the buffer can grow
                if (this.threshold == null) {
                    // the very first datum
                    this.threshold = datum;
                    this.buffer.push(datum);
                    this.unsorted = false; // a buffer of 1 is always sorted
                }
                else {
                    // not the first datum but the buffer can accommodate this datum
                    // figure out whether we need to append or prepend
                    if (this.lo) {
                        // we keep the lowest
                        if (this.threshold < datum) {
                            // we got a new highest value
                            this.threshold = datum;
                            // append new datum
                            this.buffer.push(datum);
                            // the sorted state of the buffer does not change
                        }
                        else {
                            // the value is lower then the highest thus far
                            // prepend new datum
                            this.buffer.unshift(datum);
                            // buffer might be unsorted now
                            this.unsorted = true;
                        }
                    }
                    else {
                        // we keep the highest
                        if (this.threshold > datum) {
                            // we got a new lowest value
                            this.threshold = datum;
                            // prepend new datum
                            this.buffer.unshift(datum);
                            // the sorted state of the buffer does not change
                        }
                        else {
                            // the value is higher then the lowest thus far
                            // append new datum
                            this.buffer.push(datum);
                            // buffer might be unsorted now
                            this.unsorted = true;
                        }
                    }
                }
            }
            else {
                // the buffer is at it's maximum (needed) capacity
                // we only need to process this datum in certain cases
                // case 1
                if (this.lo && this.threshold > datum) {
                    // we keep the lowest values and this value is lower then the highest value in
                    // the buffer
                    // replace the last (highest) value of the buffer with new datum
                    this.buffer[this.nMaxInternal - 1] = datum;
                    // sort the buffer in order to determine the highest value;
                    this.buffer.sort();
                    // save the highest value;
                    this.threshold = this.buffer[this.nMaxInternal - 1];
                    // buffer is sorted
                    this.unsorted = false;
                    this.sortCount++;
                    // add the removed datum to the tailings
                    this.tailing++;
                }
                // case 2
                else if (!this.lo && this.threshold < datum) {
                    // we keep the highest values and this value is higher then the lowest value in
                    // the buffer
                    // replace the first (lowest) value of the buffer with new datum
                    this.buffer[0] = datum;
                    // sort the buffer in order to determine the lowest value;
                    this.buffer.sort();
                    // save the lowest value;
                    this.threshold = this.buffer[0];
                    // buffer is sorted
                    this.unsorted = false;
                    this.sortCount++;
                    // add the removed datum to the leadings
                    this.leading++;
                }
                else {
                    // in all other cases just forget about the datum but record where the belong
                    if (this.lo) {
                        // add the datum to the tailings
                        this.tailing++;
                    }
                    else {
                        // add the datum to the leadings
                        this.leading++;
                    }
                }
            }
        }
        else {
            throw new Error(`Exceeded the ${this.nMax} size.`);
        }
    }
}
